import React from 'react';
import { getConfig } from '../config';

type CameraRow = {
  id: string;
  name: string;
  isEnabled: boolean;
  audioEnabled: boolean;
  maxConcurrentRecordings: number;
  maxRecordingTime: number;
  qrRecordingDuration: number;
  displayOrder?: number;
};

const CameraSettings: React.FC = () => {
  const { apiBase } = getConfig();
  const [workspaceId, setWorkspaceId] = React.useState<string>('');
  const [workspaces, setWorkspaces] = React.useState<{ id: number; name: string }[]>([]);
  const [rows, setRows] = React.useState<CameraRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/superadmin/workspaces/options`, { credentials: 'include' });
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as { id: number; name: string }[];
        setWorkspaces(data);
      } catch (e: any) {
        console.warn('Failed to load workspaces:', e?.message || e);
      }
    })();
  }, [apiBase]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${apiBase}/superadmin/cameras`);
      if (workspaceId) url.searchParams.set('workspaceId', workspaceId);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = (await res.json()) as CameraRow[];
      data.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      setRows(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load cameras');
    } finally {
      setLoading(false);
    }
  }, [apiBase, workspaceId]);

  React.useEffect(() => { load(); }, [load]);

  const updateField = (id: string, field: keyof CameraRow, value: any) => {
    setRows(rs => rs.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const save = async () => {
    try {
      setSaving(true);
      const payload = {
        updates: rows.map(r => ({
          id: r.id,
          isEnabled: r.isEnabled,
          audioEnabled: r.audioEnabled,
          maxConcurrentRecordings: r.maxConcurrentRecordings,
          maxRecordingTime: r.maxRecordingTime,
          qrRecordingDuration: r.qrRecordingDuration,
        })),
      };
      const res = await fetch(`${apiBase}/superadmin/cameras`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
      alert('Saved');
    } catch (e: any) {
      alert(`Save failed: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Camera Settings</h1>
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm">Workspace</label>
        <select
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">All</option>
          {workspaces.map(w => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        <button className="px-3 py-1 bg-mc-amber text-black rounded" onClick={load} disabled={loading}>Refresh</button>
        <button className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-50" onClick={save} disabled={saving || loading}>Save Changes</button>
      </div>

      {error && <div className="text-red-600 mb-2">{error}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded overflow-hidden">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Enabled</th>
              <th className="text-left p-3">Audio</th>
              <th className="text-left p-3">Max Concurrent</th>
              <th className="text-left p-3">Max Minutes</th>
              <th className="text-left p-3">QR Minutes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b last:border-none">
                <td className="p-3">{r.name}</td>
                <td className="p-3"><input type="checkbox" checked={r.isEnabled} onChange={(e) => updateField(r.id, 'isEnabled', e.target.checked)} /></td>
                <td className="p-3"><input type="checkbox" checked={r.audioEnabled} onChange={(e) => updateField(r.id, 'audioEnabled', e.target.checked)} /></td>
                <td className="p-3"><input type="number" className="border rounded px-2 py-1 w-24" value={r.maxConcurrentRecordings} onChange={(e) => updateField(r.id, 'maxConcurrentRecordings', parseInt(e.target.value || '0', 10))} /></td>
                <td className="p-3"><input type="number" className="border rounded px-2 py-1 w-24" value={r.maxRecordingTime} onChange={(e) => updateField(r.id, 'maxRecordingTime', parseInt(e.target.value || '0', 10))} /></td>
                <td className="p-3"><input type="number" className="border rounded px-2 py-1 w-24" value={r.qrRecordingDuration} onChange={(e) => updateField(r.id, 'qrRecordingDuration', parseInt(e.target.value || '0', 10))} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CameraSettings;


